import { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading } from "@/components/common";
import { FilmReel, Waveform, MusicNotes, SpeakerHigh, Plus, X, Spinner, ArrowClockwise, XCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CAP_ICON = { video: FilmReel, voice: SpeakerHigh, music: MusicNotes, sfx: Waveform };
const STATE_COLOR = {
  QUEUED: "#71717a", GENERATING: "#00E5FF", PROCESSING: "#00E5FF", QC: "#FFB800",
  COMPLETED: "#00FF9D", FAILED: "#FF3366", CANCELLED: "#8B5CF6",
};

export default function RenderPipeline() {
  const { active } = useStudio();
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [tab, setTab] = useState("video");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ capability: "video", prompt: "", useTest: false });

  const load = () => {
    api.get("/media/providers").then(({ data }) => setData(data));
    api.get("/media/jobs").then(({ data }) => setJobs(data.jobs));
  };
  useEffect(() => { load(); }, [active?.id]);

  const submit = async () => {
    try {
      const provider = form.useTest ? `test_mock_${form.capability}` : undefined;
      await api.post("/media/jobs", { capability: form.capability, prompt: form.prompt, provider });
      toast.success(form.useTest ? "TEST job queued" : "Job queued");
      setOpen(false); setForm({ capability: tab, prompt: "", useTest: false });
      const poll = (n) => setTimeout(() => { load(); if (n > 0) poll(n - 1); }, 1200);
      poll(4);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async (j) => { try { await api.post(`/media/jobs/${j.id}/cancel`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (j) => { try { await api.delete(`/media/jobs/${j.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const retry = async (j) => { try { await api.post(`/media/jobs/${j.id}/retry`); toast.success("Re-queued"); setTimeout(load, 1200); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  if (!data || !jobs) return <Loading />;
  const cap = data.capabilities.find((c) => c.id === tab);
  const shown = jobs.filter((j) => j.capability === tab);

  return (
    <div>
      <PageHeader icon={FilmReel} title="Render & Media Pipeline"
        subtitle="Provider-independent orchestration for video, voice, music and SFX. Connect a provider in Setup Center — until then jobs end honestly and no finished asset is ever simulated."
        actions={<button data-testid="new-media-job" onClick={() => { setForm({ capability: tab, prompt: "" }); setOpen(true); }} className="inline-flex items-center gap-2 rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black"><Plus size={16} weight="bold" /> New Job</button>} />

      <div data-testid="pipeline-states" className="mb-5 flex flex-wrap items-center gap-1.5">
        {data.states.map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] font-mono" style={{ color: STATE_COLOR[s] }}>{s}</span>
            {i < data.states.length - 1 && <span className="text-zinc-700">→</span>}
          </span>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {data.capabilities.map((c) => {
          const Icon = CAP_ICON[c.id];
          return (
            <button key={c.id} data-testid={`cap-tab-${c.id}`} onClick={() => setTab(c.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm border ${tab === c.id ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>
              <Icon size={15} weight="duotone" /> {c.label}
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.any_connected ? "#00FF9D" : "#666" }} />
            </button>
          );
        })}
      </div>

      <Panel data-testid="providers-panel" className="mb-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">{cap.label} providers</div>
        {!cap.any_connected && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#FFB800]/30 bg-[#FFB800]/8 px-3 py-2">
            <WarningCircle size={18} weight="fill" className="text-[#FFB800] shrink-0 mt-0.5" />
            <p className="text-xs text-[#FFE08A]"><b>{cap.label.toUpperCase()} PROVIDER NOT CONFIGURED.</b> Connect one in Setup Center to enable real generation. Jobs will fail honestly until then.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cap.providers.map((p) => (
            <div key={p.id} data-testid={`provider-${p.id}`} className="rounded-xl border border-white/8 bg-black/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-white">{p.name}</span>
                <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded-full" style={{ color: p.available ? "#00FF9D" : "#71717a", background: p.available ? "#00FF9D1a" : "#71717a1a" }}>{p.status === "NOT_CONFIGURED" ? "NOT CONFIGURED" : p.status}</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">Needs: {p.requires} · {p.where}</div>
            </div>
          ))}
        </div>
      </Panel>

      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Job queue ({shown.length})</div>
      {shown.length === 0 ? <p className="text-sm text-zinc-500">No {cap.label.toLowerCase()} jobs yet. Create one to see the pipeline run.</p> : (
        <div className="space-y-3">
          {shown.map((j) => (
            <Panel key={j.id} data-testid={`media-job-${j.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded-full" style={{ color: STATE_COLOR[j.status], background: `${STATE_COLOR[j.status]}1a` }}>{j.status}</span>
                  {j.stage && j.stage !== j.status && <span className="text-[10px] text-zinc-500 font-mono">{j.stage}</span>}
                </div>
                <p className="text-sm text-white mt-1 truncate">{j.prompt || <span className="text-zinc-500">(no prompt)</span>}</p>
                {j.is_mock && <span className="inline-block mt-1 text-[10px] font-bold uppercase font-mono px-2 py-0.5 rounded-full text-[#B794F6] bg-[#8B5CF6]/15 border border-[#8B5CF6]/40">MOCK — not a real render</span>}
                {j.error && <p className="text-[11px] text-[#FF9a9a] mt-0.5">{j.error}</p>}
              </div>
              <div className="flex gap-1.5 shrink-0">
                {j.status === "COMPLETED" && j.asset_url && <a data-testid={`asset-${j.id}`} href={`${API.replace(/\/api$/, "")}${j.asset_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#00FF9D]">View asset</a>}
                {["QUEUED", "GENERATING", "PROCESSING", "QC"].includes(j.status) && <button data-testid={`cancel-${j.id}`} onClick={() => cancel(j)} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300"><XCircle size={13} /> Cancel</button>}
                {["FAILED", "CANCELLED"].includes(j.status) && <button data-testid={`retry-${j.id}`} onClick={() => retry(j)} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#00E5FF]"><ArrowClockwise size={13} /> Retry</button>}
                {["FAILED", "CANCELLED", "COMPLETED"].includes(j.status) && <button data-testid={`delete-${j.id}`} onClick={() => del(j)} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-500 hover:text-[#FF3366]"><X size={13} /></button>}
              </div>
            </Panel>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg glass rounded-2xl p-6 animate-fadeUp">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl font-bold">New Media Job</h3><button onClick={() => setOpen(false)}><X size={20} className="text-zinc-400" /></button></div>
            <div className="space-y-3">
              <div><div className="text-xs text-zinc-500 mb-1.5">Capability</div>
                <select data-testid="job-capability" value={form.capability} onChange={(e) => setForm({ ...form, capability: e.target.value })} className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2.5 text-white [color-scheme:dark]">
                  {data.capabilities.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <textarea data-testid="job-prompt" rows={4} placeholder="Describe the shot / line / track…" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none resize-none" />
              <label data-testid="use-test-adapter" className="flex items-start gap-2 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/8 px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={form.useTest} onChange={(e) => setForm({ ...form, useTest: e.target.checked })} className="mt-0.5 accent-[#8B5CF6]" />
                <span className="text-xs text-zinc-300"><b className="text-[#B794F6]">Use TEST / MOCK adapter</b> — runs the full pipeline (QUEUED→GENERATING→PROCESSING→QC→COMPLETED) and produces a clearly-labelled placeholder. <b>Not a real render.</b></span>
              </label>
            </div>
            <button data-testid="submit-media-job" onClick={submit} className="mt-5 w-full rounded-full bg-magic py-2.5 font-semibold text-black">Queue Job</button>
          </div>
        </div>
      )}
    </div>
  );
}
